import copy from 'clipboard-copy';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import useClassy from '@core/hooks/useClassy';

import type { ButtonProps } from '@ui/Button';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import Tooltip from '@ui/Tooltip';

import classes from './style.module.scss';

type Props = Omit<ButtonProps, 'text'> & {
  /**
   * The size of the button component.
   */
  buttonSize?: 'lg' | 'md' | 'sm' | 'xs';
  /**
   * Any additional classNames you wish to add to the button.
   */
  className?: string;

  /**
   * Callback function for handling successful clipboard usage.
   */
  onCopy?: () => void;

  /**
   * Callback function for handling failed attempts to copy data to the clipboard. This'll usually get thrown for older
   * browsers.
   */
  onCopyError?: (err: Error) => void;

  /**
   * Even though `shift` is unused here or within `attributes` it is an attribute specific to
   * our `Flex` component CSS so instead of having a generic Flex-specific `HTMLAttributes` interface
   * we're defining it here.
   *
   * @todo make a generic Flex `HTMLAttributes` interface that this component extends
   * @see {@link https://readmeio.slack.com/archives/C01E8CZ6ZG9/p1653351668673939}
   */
  shift?: string;

  /**
   * Text to add into the clipboard. Can be either a string or a function (if you need to inject dynamic data).
   */
  text: string | (() => string);
};

const CopyToClipboard = React.memo(function CopyToClipboard({
  buttonSize = 'sm',
  className,
  onCopy = () => {},
  onCopyError = () => {},
  text,
  children,
  ...attributes
}: Props) {
  const { t } = useTranslation();
  const bem = useClassy(classes, 'CopyToClipboard');

  const [isCopied, setIsCopied] = useState(false);
  const [tooltipText, setTooltipText] = useState<string>(t('copyToClipboard.copyShort'));
  const timeoutDuration = 2000;

  const resetTooltip = useCallback(() => {
    setIsCopied(false);
    setTooltipText(t('copyToClipboard.copyShort'));
  }, [t]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (isCopied) {
      timer = setTimeout(() => {
        resetTooltip();
      }, timeoutDuration);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isCopied, resetTooltip]);

  const handleCopy = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const textToCopy = typeof text === 'function' ? text() : text;

    copy(textToCopy)
      .then(() => {
        setIsCopied(true);
        setTooltipText(t('copyToClipboard.copiedShort'));
        onCopy();
      })
      .catch(err => {
        setTooltipText(t('copyToClipboard.unable'));
        onCopyError(err);
      });
  };

  return (
    <Tooltip asTitle content={tooltipText} hideOnClick={false} onHidden={() => resetTooltip()} placement="bottom">
      <Button
        aria-label={t('copyToClipboard.copyFull')}
        className={bem('&', className)}
        kind="secondary"
        onClick={handleCopy}
        onKeyPress={e => {
          if (e.key === ' ' || e.key === 'Enter') {
            handleCopy(e);
          }
        }}
        size={buttonSize}
        text
        {...attributes}
      >
        <Icon aria-hidden name={isCopied ? 'check' : 'copy'} />
        {children}
      </Button>
    </Tooltip>
  );
});

export default CopyToClipboard;
